import { toast } from "@/hooks/use-toast.ts";
import { t } from "i18next";

let drawerSavePending = false;
let validationFailed = false;
let mutationOutcomes: boolean[] = [];
let finishTimer: ReturnType<typeof setTimeout> | null = null;
let drawerSaveErrorToastShown = false;

function showDrawerAppearanceSuccessToast() {
  toast({
    title: t("success"),
    description: t("cms.appearance.success"),
    variant: "success",
    duration: 2000,
  });
}

function showDrawerAppearanceErrorToast(descriptionKey: string) {
  toast({
    title: t("error"),
    description: t(descriptionKey),
    variant: "error",
    duration: 3000,
  });
}

function finalizeDrawerAppearanceSave() {
  if (!drawerSavePending) return;
  drawerSavePending = false;
  if (finishTimer) {
    clearTimeout(finishTimer);
    finishTimer = null;
  }

  if (validationFailed) {
    drawerSaveErrorToastShown = true;
    showDrawerAppearanceErrorToast("cms.appearance.error.validation");
    validationFailed = false;
    mutationOutcomes = [];
    return;
  }

  if (mutationOutcomes.length === 0) {
    // No field changed in the drawer => no mutation expected.
    // Treat as a no-op success instead of an error.
    showDrawerAppearanceSuccessToast();
    return;
  }

  const allOk = mutationOutcomes.every(Boolean);
  mutationOutcomes = [];
  if (allOk) {
    showDrawerAppearanceSuccessToast();
  } else {
    drawerSaveErrorToastShown = true;
    showDrawerAppearanceErrorToast("cms.appearance.error.save");
  }
}

/** True while a save initiated from the Apparence drawer is in progress. */
export function isDrawerAppearanceSavePending() {
  return drawerSavePending;
}

/** Avoid duplicate error toasts in page onSubmit catch after drawer save failed. */
export function consumeDrawerAppearanceSaveErrorToastShown() {
  const shown = drawerSaveErrorToastShown;
  drawerSaveErrorToastShown = false;
  return shown;
}

function scheduleFinalizeDrawerAppearanceSave() {
  if (finishTimer) clearTimeout(finishTimer);
  finishTimer = setTimeout(finalizeDrawerAppearanceSave, 120);
}

/** Call when saving from the appearance drawer (before form submit). */
export function beginDrawerAppearanceSave() {
  drawerSavePending = true;
  validationFailed = false;
  mutationOutcomes = [];
  if (finishTimer) clearTimeout(finishTimer);
  finishTimer = setTimeout(finalizeDrawerAppearanceSave, 800);
}

/** CMS page mutation completed while a drawer save is in progress. */
export function notifyDrawerAppearanceMutationResult(success: boolean) {
  if (!drawerSavePending) return;
  mutationOutcomes.push(success);
  scheduleFinalizeDrawerAppearanceSave();
}

/**
 * Form validation failed during a drawer save.
 * @returns true if the failure was handled (drawer save pending).
 */
export function notifyDrawerAppearanceValidationFailed(): boolean {
  if (!drawerSavePending) return false;
  validationFailed = true;
  finalizeDrawerAppearanceSave();
  return true;
}
